import { Grid, MenuItem, Paper, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useEffect, useRef } from 'react';

// Enable UTC plugin for dayjs
dayjs.extend(utc);
import 'react-grid-layout/css/styles.css';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ButtonVitec from '../../../app/components/ButtonVitec';
import Utils from '../../../felles/Utils';
import MenuItemTarget from '../../../models/MenuItemTarget';
import NN05Employee from '../../../models/NN05Employee';
import NN05FormEmployeeData from '../../../models/payload/NN05FormEmployeeData';
import UISize from '../../../models/UISize';
import * as actions from '../../../store/actions/index';
import * as actionsNN05 from '../../../store/actions/indexNN05';
import { AppState } from '../../../store/rootReducer';
import { useStyles } from '../styles/Form.styles';


/**
 * NN05FormUpdate Component
 * 
 * A comprehensive form component for updating employee information in the NN05 module.
 * This component provides a modal form interface with validation, date handling, and
 * location lookup functionality for editing existing employee records.
 * 
 * Author: fabrice.miras@vitecsoftware.com
 * Date: 05.08.2025
 * 
 * Features:
 * - Form validation with React Hook Form
 * - Date picker integration with dayjs
 * - Automatic location lookup by postal code
 * - Two-digit year conversion (40+ = 19xx, <40 = 20xx)
 * - Keyboard navigation support (F3, Shift+End, Escape)
 * - Click-outside-to-close functionality
 * - Real-time field validation and error display
 * - Responsive design with sandwich menu support
 * 
 * Form Fields:
 * - Employee Number (read-only display)
 * - Name (required)
 * - Address
 * - Postal code with location lookup
 * - Phone number (numeric only)
 * - Birth date with date picker
 * - Gender selection (M/K)
 * - department (max 4 chars)
 * - Position (max 20 chars)
 * - Employment start date
 * - Employment end date
 * 
 * @component
 * @example
 * // Used when transStateUpdateEmployee is set to 1
 * // Automatically loads selectedEmployee data into form
 * <NN05FormUpdate />
 * 
 * @returns {JSX.Element} The NN05FormUpdate component
 */
export default function NN05FormUpdate(/*props*/) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const screenSize: UISize = useSelector(state => (state as AppState).ui.screenSize);
  const open = screenSize.width > 0 && screenSize.width < 1200 ? false : true;
  const navigate = useNavigate();
  const n: MenuItemTarget = useSelector((state) => (state as AppState).menuItem.menuItemTarget);
  const isResetFormEnabled: boolean = useSelector((state) => (state as AppState).NN05.isResetFormEnabled);
  const date = useSelector(state => (state as AppState).accounting.date);
  const selectedEmployee: NN05Employee = useSelector((state) => (state as AppState).NN05.selectedEmployee);
  const locationByPostalCode: string = useSelector((state) => (state as AppState).NN05.locationByPostalCode);
  const listFormData: any = useSelector((state) => (state as AppState).NN05.listFormData);
  const button: boolean = useSelector((state) => (state as AppState).ui.isSandwichButtonTurnedOn)

  const methods = useForm<NN05FormEmployeeData>({
    defaultValues: {
      employeeId: '',
      name: '',
      address: '',
      postalCode: '',
      postalLocation: '',
      phoneNumber: '',
      birthDate: '',
      gender: '',
      department: '',
      position: '',
      from: dayjs(date).startOf('month'),
      to: dayjs(date).startOf('month'),
    },
    mode: 'onSubmit', // or 'onChange' if you want validation as you type
  });

  // Destructure errors from formState
  const { errors } = methods.formState;
  const containerRef = useRef<HTMLDivElement>(null);
  /**
   * Main effect hook for component initialization and cleanup
   * 
   * This effect handles:
   * - Click-outside detection to close the form
   * - Pre-population of form fields with selected employee data
   * - Date parsing and conversion from database format to dayjs objects
   * - Two-digit year conversion logic (50+ = 19xx, <50 = 20xx)
   * - Location lookup initialization
   * - Event listener cleanup on unmount
   * 
   * Date Conversion Logic:
   * - Handles day/month/year fields from database
   * - Converts 2-digit years: >=50 becomes 19xx, <50 becomes 20xx
   * - Creates properly formatted date strings for dayjs parsing
   * 
   * Dependencies: dispatch, isResetFormEnabled
   */
  useEffect(() => {
    /**
     * Handles clicks outside the form to close it
     * Ignores clicks on MUI components (menus, date pickers, modals)
     * 
     * @param {MouseEvent} event - The mouse click event
     */
    function handleClickOutside(event: MouseEvent) {
      // Ignore clicks on MUI popover/menus
      const target = event.target as HTMLElement;
      // MUI menus have role="presentation" or aria attributes, but safest is to check for the menu class
      if (
        target.closest('.MuiPopover-root') ||
        target.closest('.MuiMenu-paper') ||
        target.closest('.MuiMenu-root') ||
        target.closest('.MuiMenu-list') ||
        target.closest('.MuiPickersPopper-root') ||        // DatePicker popper
        target.closest('.MuiDateCalendar-root') ||         // DatePicker calendar
        target.closest('.MuiPickersLayout-root') ||        // DatePicker layout
        target.closest('.MuiDialog-root') ||               // DatePicker dialog (mobile)
        target.closest('.MuiModal-root')
      ) {
        return;
      }
      if (containerRef.current && !containerRef.current.contains(target)) {
        dispatch(actionsNN05.setTransStateUpdateEmployee(0));
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    //
    methods.setValue('name', selectedEmployee?.name?.trim() || '');
    methods.setValue('address', selectedEmployee?.address?.trim() || '');
    methods.setValue('postalCode', selectedEmployee?.postalCode?.trim() || '');
    methods.setValue('postalLocation', selectedEmployee?.postalLocation?.trim() || '');
    methods.setValue('phoneNumber', selectedEmployee?.phoneNumber?.trim() || '');
    methods.setValue('gender', selectedEmployee?.gender?.trim() || '', { shouldValidate: true });
    methods.setValue('department', selectedEmployee?.department?.trim() || '');
    methods.setValue('position', selectedEmployee?.position?.trim() || '');
    // Set ansatt dato (fra)
    if (selectedEmployee?.hireDateDay && !isNaN(Number(selectedEmployee.hireDateDay))) {
      const aDay = String(selectedEmployee?.hireDateDay).padStart(2, '0');
      const aMonth = String(selectedEmployee?.hireDateMonth).padStart(2, '0');
      let aYear = String(selectedEmployee?.hireDateYear).replace('.0', '');
      if (Number(aYear) >= 50) {
        aYear = '19' + aYear.padStart(2, '0');
      } else {
        aYear = '20' + aYear.padStart(2, '0'); // Add this line
      }
      const ansattDatoStr = `${aYear}-${aMonth}-${aDay}`;
      methods.setValue('from', dayjs(ansattDatoStr));
    }
    // Set sluttet dato (til) - UNCOMMENT AND FIX
    if (selectedEmployee?.terminationDateDay && !isNaN(Number(selectedEmployee.terminationDateDay))) {
      const sDay = String(selectedEmployee?.terminationDateDay).padStart(2, '0');
      const sMonth = String(selectedEmployee?.terminationDateMonth).padStart(2, '0');
      let sYear = String(selectedEmployee?.terminationDateYear).replace('.0', '');
      if (Number(sYear) >= 50) { // Use same logic as ansatt dato
        sYear = '19' + sYear.padStart(2, '0');
      } else {
        sYear = '20' + sYear.padStart(2, '0');
      }
      const sluttDatoStr = `${sYear}-${sMonth}-${sDay}`;
      methods.setValue('to', dayjs(sluttDatoStr));
    }
    // Set sluttet dato (til) - UNCOMMENT AND FIX
    if (selectedEmployee?.personNumberBirthDay && !isNaN(Number(selectedEmployee.personNumberBirthDay))) {
      const fDay = String(selectedEmployee?.personNumberBirthDay).padStart(2, '0');
      const fMonth = String(selectedEmployee?.personNumberBirthMonth).padStart(2, '0');
      let fYear = String(selectedEmployee?.personNumberBirthYear).replace('.0', '');
      if (Number(fYear) >= 50) { // Use same logic as ansatt dato
        fYear = '19' + fYear.padStart(2, '0');
      } else {
        fYear = '20' + fYear.padStart(2, '0');
      }
      const fodselsdatoStr = `${fYear}-${fMonth}-${fDay}`;
      methods.setValue('birthDate', dayjs(fodselsdatoStr));
    }
    document.addEventListener('keydown', handleKeyUpPressedAtDocumentLevel);
    //

    // On exit
    return () => {
      methods.reset();
      dispatch(actionsNN05.getLocationByPostalCodeSuccess(''));
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyUpPressedAtDocumentLevel);
      if (isResetFormEnabled)
        dispatch(actionsNN05.NN05ResetForm(true));
    };
  }, [dispatch, isResetFormEnabled]);

  // Initialize postal location from selectedEmployee (runs once on mount)
  useEffect(() => {
    if (!locationByPostalCode && selectedEmployee?.postalLocation?.trim()) {
      dispatch(actionsNN05.getLocationByPostalCodeSuccess({ postalLocation: selectedEmployee.postalLocation.trim() }));
    } else if (!locationByPostalCode && selectedEmployee?.postalCode?.trim()) {
      dispatch(actionsNN05.getLocationByPostalCode(selectedEmployee.postalCode.trim()));
    }
  }, []);
  /**
   * Handles keyboard events for form navigation and actions
   * 
   * Supported keyboard shortcuts:
   * - Shift+F7, F8: Prevented (reserved for pagination in list view)
   * - Shift+End, F3: Closes form and navigates back to main menu
   * - Escape: Cancels the form and closes it
   * 
   * Navigation Actions:
   * - Clears component store when exiting
   * - Resets menu navigation state
   * - Redirects to main menu page
   * 
   * @param {KeyboardEvent} event - The keyboard event object
   */
  const handlePressedKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const { key } = event;
    if ((event.shiftKey && key === 'F7') || key === 'F8') {
      event.preventDefault();
    }
    if ((event.shiftKey && key === 'End') || key === 'F3') {
      event.preventDefault();
      //  Reset form and component store
      dispatch(actionsNN05.NN05ClearComponentStore());
      //
      dispatch(actions.menuItemSetTarget(new MenuItemTarget(n.rootItem, n.item !== '' ? n.item : n.rootItem, '', '', n.tilpasset, n.prodSynlig
        , n.kodeSynlig, n.labelSynlig, n.menuSynlig, n.hasChildren, n.title)));
      dispatch(actions.menuItemSetSelected([n.rootItem, n.item + n.rootItem]));
      navigate(Utils.HOMEPAGE_CONTEXT + '/menu');
    }
    if (key === 'Escape' || key === 'Esc') { // Handle Escape key
      handleCancel();
      event.preventDefault();
    }
  };

  const handleKeyUpPressedAtDocumentLevel = (event: KeyboardEvent) => {
    const { key } = event;
    const actionSource = key || (event.target as HTMLInputElement).value;

    switch (true) {
      case (actionSource === 'Enter'): {
        event.preventDefault(); event.stopPropagation();
        handleSave();
        break;
      }
      case (actionSource === 'Escape'):
        event.preventDefault(); event.stopPropagation();
        handleCancel();
        break;
      default:
        return;
    }
  };

  /**
   * Cancels the form operation and resets the transaction state
   * 
   * Cleanup Actions:
   * - Sets transaction state to 0 (cancellation)
   * - Closes the form dialog
   * - Returns to the employee list view
   * 
   * Called by:
   * - Cancel button clicks
   * - Escape key presses
   * - Form abort scenarios
   */
  const handleCancel = () => {
    dispatch(actionsNN05.setTransStateUpdateEmployee(0));
  };

  /**
   * Handles form submission for employee data updates
   * 
   * Validation Processing:
   * - Validates all form fields using React Hook Form rules
   * - Converts date strings to proper Date objects
   * - Processes employment status and category changes
   * 
   * Data Transformation:
   * - Converts two-digit years using 40-year cutoff logic
   * - Formats dates for API compatibility
   * - Handles empty/null field values appropriately
   * 
   * @param {NN05FormEmployeeData | any} data - Form data object containing employee information
   */
  // Function to handle form submission
  const onSubmit = (data: NN05FormEmployeeData) => {
    data.employeeId = selectedEmployee.employeeId;
    data.postalLocation = locationByPostalCode;
    
    // Format dates as YYYY-MM-DD strings to prevent timezone shifting during serialization
    
    if (data.birthDate) {
      data.birthDate = dayjs(data.birthDate).format('YYYY-MM-DD') as any;
    }
    if (data.from) {
      data.from = dayjs(data.from).format('YYYY-MM-DD') as any;
    }
    if (data.to) {
      data.to = dayjs(data.to).format('YYYY-MM-DD') as any;
    }
    
    dispatch(actionsNN05.setTransUpdateEmployee({ data, listFormData }));
  };

  /**
   * Initiates the save process by triggering form validation and submission
   * 
   * Process Flow:
   * - Triggers React Hook Form validation for all fields
   * - If validation passes, calls onSubmit with form data
   * - If validation fails, displays error messages to user
   * 
   * Validation Checks:
   * - Employee ID uniqueness and format
   * - Required field completion (name)
   * - Postal code format validation
   * - Data type consistency
   * 
   * Integration:
   * - Connected to save button click events
   * - Uses methods.handleSubmit to leverage form validation
   * - Ensures data integrity before API calls
   */
  // Function to handle save action
  const handleSave = () => {
    methods.handleSubmit(onSubmit)();
  };

  // Render the component
  return (
    <div className={classes.component} >
      <div className={classes.bg} />
      <div className={clsx(classes.content, { [classes.contentShift]: button ? true : open })}>
        <div ref={containerRef} className={classes.root} tabIndex={0} onKeyDown={(e) => handlePressedKey(e)} style={{
        }}>
          <Paper elevation={10} className={classes.canvas}>
            <FormProvider {...methods}>
              <form>
                <Grid container direction="column" sx={{ height: '100%', width: '100%' }}>
                  {/* Header */}
                  <Grid sx={{
                    flex: '0 0 auto',
                    padding: '25px 0 0 0', // Top padding only
                    display: 'flex',
                    alignItems: 'flex-start', // Align to top
                    justifyContent: 'center',
                  }}>
                    {/* Place your header content here */}
                    <h2 style={{ margin: 0 }}>Editering av persondata</h2>
                  </Grid>
                  {/* Form */}
                  <Grid sx={{ flex: '1 1 auto', padding: 3, display: 'flex', justifyContent: 'center' }}>
                    {/* Place your form fields here */}
                    <Grid container spacing={2.4}>
                      {/* Row 1: Ansatt nr. | Navn */}
                      <Grid size={{ xs: 12, sm: 3 }}>
                        <TextField label="AnsattNr." autoComplete="off" type="text"
                          className={classes.tField} value={selectedEmployee?.employeeId}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 9 }}>
                        <TextField label="Navn" autoComplete="off" className={classes.tField} error={!!errors.name}
                          autoFocus
                          {...methods.register("name", { required: "Vennligst skriv inn navnet." })}
                          onChange={(e) => {
                            methods.setValue(`name`, (e.target as any).value);
                            methods.trigger("name");
                          }}
                        />
                      </Grid>
                      {/* Row 2: Adresse */}
                      <Grid size={{ xs: 12 }}>
                        <TextField label="Adresse" autoComplete="off" className={classes.tField}
                          {...methods.register("address", { required: false })}
                          onChange={(e) => { methods.setValue(`address`, (e.target as any).value) }}
                        />
                      </Grid>
                      {/* Spacer */}
                      {/* Row 3: Post / Sted */}
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField
                          label="Post"
                          autoComplete="off"
                          type="text"
                          className={classes.tField}
                          error={!!errors.postalCode}
                          {...methods.register("postalCode", {
                            required: "Må være 4 siffer",
                            pattern: { value: /^[0-9]{4}$/, message: "Må være 4 siffer" },
                            validate: (value) => {
                              if (value.length !== 4) return "Må være 4 siffer";
                              return true;
                            }, maxLength: 4
                          })}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, '');
                            methods.setValue('postalCode', value);
                            methods.trigger("postalCode");
                            if (value.length === 4) {
                              dispatch(actionsNN05.getLocationByPostalCode(value));
                            } else {
                              dispatch(actionsNN05.getLocationByPostalCodeSuccess(''));
                            }
                          }}
                          InputProps={{ inputProps: { inputMode: 'numeric', pattern: '[0-9]*', maxLength: 4 } }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 8 }}>
                        <input type="hidden" {...methods.register("postalLocation")} name="postalLocation"
                          onChange={(e) => { methods.setValue(`postalLocation`, locationByPostalCode) }}
                        />
                        <TextField type="text" className={classes.tField} value={locationByPostalCode} InputProps={{
                          readOnly: true,
                          disableUnderline: false,
                          sx: {
                            border: 'none', background: 'transparent', fontSize: '1.3em', marginTop: 1, alignSelf: 'flex-end',
                          }
                        }}
                          variant="standard" fullWidth />
                      </Grid>
                      {/* Row 4: Telefon | Fødselsdato | Kjønn */}
                      <Grid container spacing={2} alignItems="flex-end" sx={{ width: '100%', marginLeft: 0, marginTop: 0 }}>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <TextField label="Telefon" autoComplete="off" type="text" className={classes.tField}
                            {...methods.register("phoneNumber", { required: false, pattern: /^[0-9]*$/, maxLength: 10 })}
                            onChange={(e) => { const value = e.target.value.replace(/[^0-9]/g, ''); methods.setValue('phoneNumber', value); }}
                            InputProps={{ inputProps: { inputMode: 'numeric', pattern: '[0-9]*', maxLength: 8, }, }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 5 }}>
                          <Controller
                            control={methods.control}
                            name={`birthDate`}
                            rules={{ required: "Fødselsdato er påkrevd" }}
                            render={({ field: { onChange/*, value*/ } }) => (
                              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                                <DatePicker label="Fødselsdato" sx={{ width: '100%', marginTop: '8px' }} className={classes.tField}
                                  openTo="day" disableFuture={false}
                                  value={dayjs(methods.getValues('birthDate'))}
                                  views={["year", "month", "day"]}
                                  onChange={(item) => {
                                    // Ensure we keep the date as-is without timezone conversion
                                    const normalizedDate = item;// ? item.startOf('day') : null;
                                    onChange(normalizedDate);
                                    methods.setValue('birthDate', normalizedDate as any);
                                  }}
                                />
                              </LocalizationProvider>
                            )}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 3 }}>
                          <Controller
                            control={methods.control}
                            name="gender"
                            rules={{ required: "Kjønn er påkrevd" }}
                            render={({ field }) => (
                              <TextField
                                select
                                label="Kjønn"
                                autoComplete="off"
                                className={classes.tField}
                                error={!!errors.gender}
                                value={field.value || ""}
                                onChange={field.onChange}
                                InputProps={{ inputProps: { maxLength: 1 } }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MenuItem value="M">M</MenuItem>
                                <MenuItem value="K">K</MenuItem>
                              </TextField>
                            )}
                          />
                        </Grid>
                      </Grid>
                      <Grid size={{ xs: 9, sm: 4 }}>
                        <TextField label="Avdeling" autoComplete="off" className={classes.tField}
                          error={!!errors.department}
                          {...methods.register("department", { required: "Avdeling er påkrevd" })}
                          onChange={(e) => {
                            methods.setValue(`department`, (e.target as any).value);
                            methods.trigger("department");
                          }}
                          InputProps={{ inputProps: { maxLength: 4 } }}
                        />
                      </Grid>
                      {/* Row 5: Avdeling | Stilling */}
                      <Grid size={{ xs: 15, sm: 8 }}>
                        <TextField label="Stilling" autoComplete="off" className={classes.tField}
                          {...methods.register("position", { required: false })}
                          onChange={(e) => { methods.setValue(`position`, (e.target as any).value) }}
                          InputProps={{ inputProps: { maxLength: 20 } }}
                        />
                      </Grid>
                      {/* Row 6: Ansatt dato | Sluttet dato */}
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Controller control={methods.control} name={`from`} render={({ field: { onChange/*, value*/ } }) => (
                          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                            <DatePicker label="Ansatt dato" sx={{ width: '100%', marginTop: '8px' }} className={classes.tField}
                              openTo="day" disableFuture={false}

                              value={dayjs(methods.getValues('from'))}
                              views={["year", "month", "day"]}
                              onChange={(item) => {
                                const normalizedDate = item ? dayjs.utc(item.format('YYYY-MM-DD')) : null;
                                onChange(normalizedDate);
                                methods.setValue('from', normalizedDate as any);
                              }}
                            />
                          </LocalizationProvider>
                        )}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Controller control={methods.control} name={`to`} render={({ field: { onChange/*, value*/ } }) => (
                          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                            <DatePicker label="Sluttet dato" sx={{ width: '100%', marginTop: '8px' }} className={classes.tField}
                              openTo="day" disableFuture={false}
                              minDate={dayjs().set('year', 2024).set('month', 0)}
                              maxDate={dayjs(date).add(1, 'year').endOf('year')}
                              value={dayjs(methods.getValues('to'))}
                              views={["year", "month", "day"]}
                              onChange={(item) => {
                                const normalizedDate = item ? dayjs.utc(item.format('YYYY-MM-DD')) : null;
                                onChange(normalizedDate);
                                methods.setValue('to', normalizedDate as any);
                              }}
                            />
                          </LocalizationProvider>
                        )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  {/* Footer */}
                  <Grid sx={{
                    flex: '0 0 auto', marginTop: 'auto', paddingBottom: 2, display: 'flex', justifyContent: 'center',
                    alignItems: 'center',
                  }} className={classes.formError}>
                    {errors.name
                      ? <div>{errors.name.message}</div>
                      : errors.postalCode
                        ? <div>{errors.postalCode.message}</div>
                        : errors.birthDate
                          ? <div>{errors.birthDate.message}</div>
                          : errors.gender
                            ? <div>{errors.gender.message}</div>
                            : errors.department
                              ? <div>{errors.department.message}</div>
                              : <div>&nbsp;</div>
                    }
                  </Grid>
                  <Grid sx={{
                    flex: '0 0 auto', marginTop: 'auto', padding: 0, display: 'flex', justifyContent: 'center',
                    alignItems: 'top', gap: 5, height: 60, width: '100%',
                  }}>
                    {/* Place your footer actions here */}
                    <ButtonVitec _label="Lagre" _type="button" _padding={0} _onclick={handleSave} />
                    <ButtonVitec _label="Avbryt" _type="button" _padding={0} _onclick={handleCancel} />
                  </Grid>
                </Grid>
              </form>
            </FormProvider>
          </Paper>
        </div>
      </div>
    </div >
  );
}